import { usePublishMenuItems, PublishMenuItemsProps } from "../../../publish/publish-menu/usePublishMenuItems";
import { Button, ButtonProps, Menu, useMenu } from "@kaltura/ds-react-components";
import { Globe24Icon, Link24Icon, Lock24Icon } from "@kaltura/ds-react-icons";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { ReactElement } from "react";
import { ButtonClickAnalyticsType, PublishStatus } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

export interface PublishMenuButtonProps extends PublishMenuItemsProps {
    variant?: ButtonProps["variant"];
    label?: string;
    icon?: ReactElement;
    buttonAnalyticsActionName?: string;
    isLoading?: boolean;
    disabled?: boolean;
    entryName?: string;
}

/**
 * publish menu button
 * used in:
 * my-media bulk action (desktop), upload media bulk action,
 * upload media single entry action, express recorder single entry action (see AsyncPublishMenuButton)
 */
export function PublishMenuButton(props: PublishMenuButtonProps) {
    const {
        variant = "borderless",
        label,
        icon,
        buttonAnalyticsActionName,
        entryIds,
        isLoading = false,
        disabled,
        entryName,
        ...rest
    } = props;

    const entryId = entryIds.length === 1 ? entryIds[0] : undefined;

    const sendButtonAnalytics = useButtonAnalytics();

    const { close, buttonProps, menuProps } = useMenu({
        menuId: "publish",
        onOpen: () => {
            if (buttonAnalyticsActionName) {
                sendButtonAnalytics(buttonAnalyticsActionName, ButtonClickAnalyticsType.CHOOSE, entryId);
            }
        },
    });

    const getPublishIcon = (publishStatus?: PublishStatus) => {
        switch (publishStatus) {
            case PublishStatus.UNLISTED:
                return <Link24Icon />;
            case PublishStatus.PRIVATE:
                return <Lock24Icon />;
            default:
                return <Globe24Icon />;
        }
    };

    const getPublishLabel = (publishStatus?: PublishStatus) => {
        switch (publishStatus) {
            case PublishStatus.UNLISTED:
                return translate("Unlisted");
            case PublishStatus.PRIVATE:
                return translate("Private");
            default:
                return translate("Published");
        }
    };

    const items = usePublishMenuItems({
        entryIds,
        onCloseMenu: close,
        ...rest,
    });

    /**
     * Generates an accessible aria-label based on entry name and publish status.
     * - When publishStatus is loaded: "{PublishStatus} - Publish options for entry {entryName}"
     *   (e.g., "Published - Publish options for entry My Video")
     * - During loading (publishStatus undefined): "Publish options for entry {entryName}"
     * - If entryName is not provided: no aria-label is set (button relies on visible label)
     */
    const buildAriaLabel = () => {
        if (entryName && rest.publishStatus !== undefined) {
            const statusLabel = getPublishLabel(rest.publishStatus);
            return translate("%1 - Publish options for entry %2", [statusLabel, entryName]);
        }
        if (entryName) {
            return translate("Publish options for entry %1", [entryName]);
        }
        return undefined;
    };

    return (
        <>
            <Button
                {...buttonProps}
                className={"kms-ds-publish-menu-button"}
                variant={variant}
                color={"secondary"}
                startIcon={icon || getPublishIcon(rest.publishStatus)}
                loading={isLoading}
                aria-label={buildAriaLabel()}
                disabled={isLoading || disabled}
            >
                {label || getPublishLabel(rest.publishStatus)}
            </Button>
            <Menu {...menuProps}>{items}</Menu>
        </>
    );
}

export default PublishMenuButton;
